import { useEffect } from "preact/hooks";

export type PillPickerProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  values: Record<string, string>;
};

const scrollToCenter = (container: HTMLElement | null | undefined, target: HTMLElement): void => {
  if (!container) {
    return;
  }
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const offset = targetRect.left + targetRect.width / 2 - (containerRect.left + containerRect.width / 2);
  container.scrollTo({ left: container.scrollLeft + offset, behavior: "smooth" });
};

const SELECTED = "bg-stone-900 text-white dark:bg-white dark:text-stone-900";
const IDLE = "bg-stone-100 text-stone-700 hover:bg-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700";

/** A row of pills that scrolls sideways and keeps the chosen one centred. */
export const PillPicker = ({ id, values, value: selected, onChange }: PillPickerProps) => {
  useEffect(() => {
    const pill = document.getElementById(id + selected);
    if (pill) {
      scrollToCenter(pill.parentElement?.parentElement, pill);
    }
  }, [selected, id, values]);

  return (
    <div class="w-full overflow-x-auto scrollbar-hide" id={id}>
      <div class="flex gap-3 min-w-max">
        {Object.entries(values).map(([value, name]) => (
          <button
            type="button"
            id={id + value}
            key={value}
            onClick={() => value !== selected && onChange(value)}
            class={`rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 px-4 py-2 ${value === selected ? SELECTED : IDLE}`}
            data-test-click="pill"
            data-test-id={id}
            data-test-value={value}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
};
